import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Zap, Tag, Check, X, Mail } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function AuthPage() {
  const { login, register, googleLogin, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') === 'register' ? 'signup' : 'login';
  const showForgot = searchParams.get('forgot') === 'true';

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regStoreName, setRegStoreName] = useState('');
  const [regPromoCode, setRegPromoCode] = useState('');
  const [promoValid, setPromoValid] = useState(null);
  const [regLoading, setRegLoading] = useState(false);

  const [forgotOpen, setForgotOpen] = useState(showForgot);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (user) {
      if (user.role === 'platform_admin') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      await login(loginEmail, loginPassword);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegLoading(true);
    try {
      await register(regEmail, regPassword, regName, regStoreName, regPromoCode || undefined);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setRegLoading(false);
    }
  };

  const validatePromo = async (code) => {
    if (!code || code.length < 3) { setPromoValid(null); return; }
    try {
      await api.post('/promo/validate', { code });
      setPromoValid(true);
    } catch {
      setPromoValid(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail.trim()) { toast.error('Enter your email'); return; }
    setForgotLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      setForgotSent(true);
      toast.success('Reset email sent!');
    } catch {
      toast.error('Failed to send reset email');
    } finally { setForgotLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="auth-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2.5 mb-8 animate-fade-in-up">
          <Zap className="w-10 h-10 text-orange-500" />
          <span className="text-3xl font-extrabold tracking-tighter" style={{ fontFamily: 'Manrope, sans-serif' }}>
            EmergencyPOS
          </span>
        </div>

        <p className="text-center text-slate-500 text-sm mb-6 animate-fade-in-up stagger-1">
          Emergency payment solution for when your terminal is down
        </p>

        <Card className="animate-fade-in-up stagger-2 border-slate-200 shadow-sm">
          <Tabs defaultValue={defaultTab}>
            <CardHeader className="pb-2">
              <TabsList className="grid w-full grid-cols-2" data-testid="auth-tabs">
                <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="signup" data-testid="signup-tab">Sign Up</TabsTrigger>
              </TabsList>
            </CardHeader>
            <CardContent className="pt-4">
              <TabsContent value="login" className="mt-0">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="merchant@example.com"
                      value={loginEmail}
                      onChange={e => setLoginEmail(e.target.value)}
                      required
                      className="h-12 text-base"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">Password</Label>
                      <button
                        type="button"
                        onClick={() => setForgotOpen(true)}
                        className="text-xs text-orange-500 hover:text-orange-600 font-medium"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <Input
                      id="login-password"
                      type="password"
                      value={loginPassword}
                      onChange={e => setLoginPassword(e.target.value)}
                      required
                      className="h-12 text-base"
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide"
                    disabled={loginLoading}
                    data-testid="login-submit-btn"
                  >
                    {loginLoading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="signup" className="mt-0">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reg-name">Full Name</Label>
                    <Input
                      id="reg-name"
                      value={regName}
                      onChange={e => setRegName(e.target.value)}
                      required
                      className="h-12 text-base"
                      data-testid="signup-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-email">Email</Label>
                    <Input
                      id="reg-email"
                      type="email"
                      value={regEmail}
                      onChange={e => setRegEmail(e.target.value)}
                      required
                      className="h-12 text-base"
                      data-testid="signup-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-password">Password</Label>
                    <Input
                      id="reg-password"
                      type="password"
                      value={regPassword}
                      onChange={e => setRegPassword(e.target.value)}
                      required
                      className="h-12 text-base"
                      data-testid="signup-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-store">Store Name</Label>
                    <Input
                      id="reg-store"
                      value={regStoreName}
                      onChange={e => setRegStoreName(e.target.value)}
                      placeholder="My Store"
                      className="h-12 text-base"
                      data-testid="signup-store-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-promo" className="flex items-center gap-1.5">
                      <Tag className="w-3.5 h-3.5" /> Promo Code <span className="text-xs text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="reg-promo"
                        value={regPromoCode}
                        onChange={e => {
                          const val = e.target.value.toUpperCase();
                          setRegPromoCode(val);
                          validatePromo(val);
                        }}
                        placeholder="EPOS7FREE"
                        className={`h-12 text-base font-mono uppercase pr-10 ${promoValid === true ? 'border-emerald-500' : promoValid === false ? 'border-red-400' : ''}`}
                        data-testid="signup-promo-input"
                      />
                      {promoValid === true && <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />}
                      {promoValid === false && <X className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-400" />}
                    </div>
                    {promoValid === true && <p className="text-xs text-emerald-600 font-medium">7 days free trial - no platform fees!</p>}
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide"
                    disabled={regLoading}
                    data-testid="signup-submit-btn"
                  >
                    {regLoading ? 'Creating account...' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>

              {/* Google Login */}
              <div className="mt-5">
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-muted-foreground">or</span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="w-full h-12 border-slate-200 text-slate-700 font-medium"
                  onClick={googleLogin}
                  data-testid="google-login-btn"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </Button>
              </div>
            </CardContent>
          </Tabs>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6">
          Secure payments powered by Stripe. No card data stored.
        </p>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onOpenChange={(o) => { setForgotOpen(o); if (!o) { setForgotSent(false); setForgotEmail(''); } }}>
        <DialogContent className="sm:max-w-sm" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle>{forgotSent ? 'Check Your Email' : 'Forgot Password'}</DialogTitle>
          </DialogHeader>
          {forgotSent ? (
            <div className="text-center py-4">
              <Mail className="w-10 h-10 text-orange-500 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">If an account exists for <strong>{forgotEmail}</strong>, you will receive a reset link shortly.</p>
              <Button variant="outline" onClick={() => { setForgotOpen(false); setForgotSent(false); }} className="mt-4" data-testid="forgot-close-btn">Close</Button>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Enter your email and we'll send you a password reset link.</p>
                <Input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="your@email.com" className="h-12" data-testid="forgot-email-input" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setForgotOpen(false)}>Cancel</Button>
                <Button onClick={handleForgotPassword} disabled={forgotLoading} className="bg-orange-500 hover:bg-orange-600" data-testid="forgot-submit-btn">
                  {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
